import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Modal,
  Row,
  Col,
  Checkbox,
} from "antd";
import { ActivityProps } from "../../interfaces/interfaces";
import { useContext, useEffect, useState } from "react";
import DataContext from "../../context/DataContext";
import { ACTIVITY_INITIAL_STATE } from "../../utils/data";
import dayjs from "dayjs";
import { TagSelector } from "../ui";
import { filterOption } from "../../utils/func/utils";

interface Props {
  modalStatus: {
    open: boolean;
    create: boolean;
    update: boolean;
    payload?: ActivityProps | null;
  } | null;
  onCancel: () => void;
  onSubmit: (values: ActivityProps) => void;
}

export const ModalCreateOrUpdateActivity = ({
  modalStatus,
  onCancel,
  onSubmit,
}: Props) => {
  const [form] = Form.useForm();
  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [values, setValues] = useState<ActivityProps>(ACTIVITY_INITIAL_STATE);
  const [isMultipleCustomers, setisMultipleCustomers] =
    useState<boolean>(false);
  const {
    usersList,
    userOptions,
    customerOptions,
    customerByClient,
    projectList,
  } = useContext(DataContext);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setisMultipleCustomers(
      Boolean(
        modalStatus?.payload?.customers &&
          Array.isArray(modalStatus?.payload?.customers) &&
          modalStatus?.payload?.customers?.length > 0,
      ),
    );
  }, [modalStatus?.payload?.customers]);

  useEffect(() => {
    if (customerByClient) {
      let customerProjects = customerByClient.find(
        (customer) =>
          customer._id ===
          (typeof values.customer === "object" && values.customer !== null
            ? values.customer._id
            : values.customer),
      )?.Projects;
      if (customerProjects) {
        setProjectOptions(customerProjects);
        const projectId =
          typeof values.project === "object" && values.project !== null
            ? values.project._id
            : values.project;
        const foundProject =
          customerProjects.find((p) => p.id._id === projectId)?.id || null;
        setValues((prev) => ({
          ...prev,
          project: foundProject ?? null,
        }));
      } else {
        setProjectOptions([]);
      }
    }
  }, [customerByClient, values.customer, values.project]);

  useEffect(() => {
    if (values) {
      form.setFieldsValue(values);
    } else {
      form.setFieldsValue(ACTIVITY_INITIAL_STATE);
      setValues(ACTIVITY_INITIAL_STATE);
    }
  }, [values]);

  useEffect(() => {
    if (modalStatus && modalStatus.payload) {
      setValues(modalStatus.payload);
    } else {
      setValues(ACTIVITY_INITIAL_STATE);
    }
  }, [modalStatus]);

  return (
    <Modal
      title="Activity"
      destroyOnClose
      zIndex={2000}
      open={modalStatus?.open || false}
      width={1000}
      onOk={() => {
        onCancel();
        form.resetFields();
        setValues(ACTIVITY_INITIAL_STATE);
      }}
      onCancel={() => {
        onCancel();
        form.resetFields();
        setValues(ACTIVITY_INITIAL_STATE);
      }}
      footer={null}
      centered
    >
      <Form
        layout="vertical"
        onFinish={() => {
          if (isLoading) return; // Prevent duplicate submissions
          setIsLoading(true);
          onSubmit({ ...modalStatus?.payload, ...values });
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        }}
        form={form}
        initialValues={values}
      >
        <Row gutter={24}>
          <Col lg={8} md={12} sm={12} xs={12}>
            <Form.Item label="Customer">
              <Select
                allowClear
                showSearch
                mode={isMultipleCustomers ? "multiple" : undefined}
                disabled={isMultipleCustomers}
                optionFilterProp="children"
                filterOption={filterOption}
                options={
                  values?.customers?.map((customer) => ({
                    label: customer.DisplayName || "",
                    value: customer._id,
                  })) || customerOptions
                }
                placeholder={
                  isMultipleCustomers
                    ? "All Customers assigned"
                    : "Select Customer"
                }
                value={
                  values.customer?.hasOwnProperty("_id")
                    ? values.customer._id
                    : values.customer
                }
                onChange={(value) => {
                  if (!value) {
                    setValues({
                      ...values,
                      customer: value as any,
                    });
                  } else {
                    setValues({
                      ...values,
                      customer: value as any,
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={8} md={12} sm={12} xs={12}>
            <Form.Item label="Project">
              <Select
                showSearch
                allowClear
                disabled={!values.customer || isMultipleCustomers}
                placeholder="Select project"
                options={
                  isMultipleCustomers || !values.customer
                    ? projectList.map((p) => ({ label: p.name, value: p._id }))
                    : projectOptions.map((p) => ({
                        label: p.id?.name,
                        value: p.id?._id,
                      }))
                }
                value={values.project?._id || values.project}
                filterOption={filterOption}
                onChange={(value) => {
                  if (!value) {
                    setValues({
                      ...values,
                      project: value as any,
                    });
                  } else {
                    const selectedProject = projectOptions.find(
                      (p) => p.id._id === value,
                    );
                    setValues({
                      ...values,
                      project: selectedProject?.id || null,
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={8} md={12} sm={12} xs={12}>
            <Form.Item
              label="Type of Activity"
              name="typeOfTask"
              rules={[
                { required: true, message: "Please select an activity type" },
              ]}
            >
              <Select
                filterOption={filterOption}
                onChange={(value) =>
                  setValues({ ...values, typeOfTask: value })
                }
                showSearch
                options={[
                  { label: "Call", value: "Call" },
                  { label: "Meeting", value: "Meeting" },
                  { label: "Follow-up", value: "Follow-up" },
                  { label: "Email", value: "Email" },
                  { label: "Other", value: "Other" },
                ]}
                placeholder="Select type"
              />
            </Form.Item>
          </Col>
          <Col lg={8} md={12} sm={12} xs={12}>
            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Description is required" }]}
            >
              <Input
                placeholder="Enter activity description"
                onChange={(event) =>
                  setValues({ ...values, description: event.target.value })
                }
              />
            </Form.Item>
          </Col>
          <Col lg={12} md={12} sm={12} xs={12}>
            <Form.Item label="Time Logged">
              <DatePicker.RangePicker
                showTime={{ format: "HH:mm" }}
                popupStyle={{ position: "fixed", top: 250, left: 250 }}
                format="MM/DD/YYYY HH:mm"
                value={
                  values.startDate && values.endDate
                    ? [dayjs(values.startDate), dayjs(values.endDate)]
                    : undefined
                }
                onChange={(value) => {
                  if (value) {
                    setValues({
                      ...values,
                      startDate: value[0],
                      endDate: value[1],
                    });
                  }
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col lg={4} md={12} sm={12} xs={12}>
            <Form.Item name="Billable" valuePropName="checked" label=" ">
              <Checkbox
                onChange={(e) =>
                  setValues({ ...values, isBillable: e.target.checked })
                }
              >
                Is Billable
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={6} md={12} sm={12} xs={12}>
            <Form.Item label="Assigned To">
              <Select
                filterOption={filterOption}
                showSearch
                maxTagCount="responsive"
                mode="multiple"
                value={
                  Array.isArray(values.assignedTo)
                    ? values.assignedTo.map((user) => user._id)
                    : values.assignedTo?._id
                      ? [values.assignedTo?._id]
                      : null
                }
                placeholder="Assign to user"
                options={userOptions}
                onChange={(value) => {
                  const selected = usersList.filter((user) =>
                    value.includes(user._id as any),
                  );
                  setValues({ ...values, assignedTo: selected });
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col lg={6} md={12} sm={12} xs={12}>
            <Form.Item label="Tags">
              <TagSelector
                selectedTags={values.tags || []}
                setSelectedTags={(selectedTags) => {
                  setValues({ ...values, tags: selectedTags });
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={6} md={12} sm={12} xs={12}>
            <Form.Item label="Priority" name="priority">
              <Select
                filterOption={filterOption}
                showSearch
                onChange={(value) => setValues({ ...values, priority: value })}
                options={[
                  { label: "Low", value: "Low" },
                  { label: "Medium", value: "Medium" },
                  { label: "High", value: "High" },
                ]}
                placeholder="Select priority"
              />
            </Form.Item>
          </Col>
          <Col lg={6} md={12} sm={12} xs={12}>
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select a status" }]}
            >
              <Select
                filterOption={filterOption}
                showSearch
                onChange={(value) => setValues({ ...values, status: value })}
                options={[
                  { label: "Planned", value: "Planned" },
                  { label: "In Progress", value: "In Progress" },
                  { label: "Completed", value: "Completed" },
                ]}
                placeholder="Select status"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item label="Notes" name="notes">
              <Input.TextArea
                rows={2}
                placeholder="Additional notes"
                onChange={(event) =>
                  setValues({ ...values, notes: event.target.value })
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Buttons */}
        <Row justify="end">
          <Col>
            <Button
              type="default"
              onClick={onCancel}
              style={{ marginRight: 8 }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" disabled={isLoading}>
              Save Activity
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
